import { useCallback, useRef, useState } from 'react';
import { PageLayoutSidebar } from '../PageLayout';
import { MenuFoldOutIcon, MenuFoldInIcon } from '@/components/Icons';
import SidebarMenu from './SidebarMenu';
import { Link, useNavigate, useLocation, useMatch } from 'react-router-dom';
import { Icon } from '../primitives/icon/Icon';
import AccountSettingsTabs from './AccountSettingsTabs';
import { CreateDropdownButton } from './CreateDropdownButton';
import { useUserContext } from '@/contexts/UserContext';

const SIDEBAR_COLLAPSED_WIDTH = 56;
const SIDEBAR_DEFAULT_WIDTH = 260;
const SIDEBAR_MAX_WIDTH = 600;

/** Measure how much wider the sidebar needs to be so no text is truncated, capped at SIDEBAR_MAX_WIDTH. */
export function measureNeededWidth(container: HTMLElement, currentWidth: number): number {
  const els = container.querySelectorAll<HTMLElement>('[class*="text-ellipsis"]');
  let maxOverflow = 0;
  els.forEach((el) => {
    const overflow = el.scrollWidth - el.clientWidth;
    if (overflow > maxOverflow) maxOverflow = overflow;
  });
  if (maxOverflow === 0) return currentWidth;
  // Add 16px padding so text doesn't sit flush against the edge
  return Math.min(SIDEBAR_MAX_WIDTH, currentWidth + maxOverflow + 16);
}

export function Sidebar() {
  const { sidebarCollapsed, setValue } = useUserContext();
  const [customWidth, setCustomWidth] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isSettingsRoute = useMatch('/settings/*');
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  const sidebarWidth = sidebarCollapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : customWidth ?? SIDEBAR_DEFAULT_WIDTH;

  const toggleSidebar = useCallback(
    (collapsed: boolean) => {
      if (collapsed) setCustomWidth(null);
      setValue((prev) => ({ ...prev, sidebarCollapsed: collapsed }));
    },
    [setValue],
  );

  const handleEdgeClick = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const currentW = el.getBoundingClientRect().width;
    const needed = measureNeededWidth(el, currentW);
    if (needed > currentW) {
      // Still has truncated text → expand further
      setCustomWidth(needed);
    } else if (customWidth) {
      // Nothing truncated and already expanded → collapse back
      setCustomWidth(null);
    }
  }, [customWidth]);

  return (
    <PageLayoutSidebar className="text-xs text-content-primary pt-4 flex flex-col border-r border-default bg-surface relative">
      <div ref={contentRef} style={{ width: sidebarWidth }} className="transition-all duration-300">
        {sidebarCollapsed ? (
          <CollapsedSidebar onExpand={() => toggleSidebar(false)} />
        ) : isSettingsRoute ? (
          <SettingsSidebar onCollapse={() => toggleSidebar(true)} />
        ) : (
          <SidebarMenu
            onSidebarOpenClose={toggleSidebar}
            sidebarOpen={!sidebarCollapsed}
            search={search}
            pathname={pathname}
            navigate={navigate}
          />
        )}
      </div>
      {!sidebarCollapsed && (
        <div
          role="button"
          tabIndex={0}
          onClick={handleEdgeClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleEdgeClick();
          }}
          className="absolute top-0 -right-[2px] w-[5px] h-full cursor-pointer z-10 group"
        >
          <div className="w-px h-full mx-auto transition-colors bg-transparent group-hover:bg-blue-400" />
        </div>
      )}
    </PageLayoutSidebar>
  );
}

function CollapsedSidebar({ onExpand }: { onExpand: () => void }) {
  return (
    <div className="flex flex-col justify-between h-[90vh] w-[56px]">
      <div className="flex items-center justify-center pt-4">
        <MenuFoldInIcon
          className="w-4 h-4 shrink-0 text-content-primary cursor-pointer mt-[1px]"
          onClick={onExpand}
        />
      </div>
      <div className="flex flex-col items-center justify-center w-[56px]">
        <div className="w-[32px] bg-border-default h-px mb-4 mx-4"></div>
        <CreateDropdownButton variant="compact" />
      </div>
    </div>
  );
}

function SettingsSidebar({ onCollapse }: { onCollapse: () => void }) {
  return (
    <>
      <div className="flex items-center justify-between">
        <Link to="/" className="btn btn-primary flex ml-4 text-lg font-light">
          <Icon name="left" className="mr-3 mt-1"></Icon> <span className="">Back</span>
        </Link>
        <MenuFoldOutIcon
          className="w-4 h-4 shrink-0 text-content-primary cursor-pointer mr-3"
          onClick={onCollapse}
        />
      </div>
      <div className="flex mb-8 ml-6 my-8 text-content-primary text-sm">
        <Icon name="gear" className="h-4 w-4 mt-[2px]" />
        <span className="ml-2">Account Settings</span>
      </div>
      <div className="mt-8 bg-surface flex">
        <AccountSettingsTabs />
      </div>
    </>
  );
}
